import {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Stack,
    Typography
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';

import MessageBox from '../common/box/MessageBox';
import {initializeDetail, readIdeaDetail} from '../../modules/idea/idea';

export default function IdeaDetailModal({ open, ideaId, onClose }) {
    const dispatch = useDispatch();

    const { detailResponse, detailErrorResponse } = useSelector((state) => state.idea);
    const loading = useSelector(({ loading }) => loading['idea/READ_IDEA_DETAIL']);

    useEffect(() => {
        if (open && ideaId != null) {
            dispatch(readIdeaDetail({ ideaId }));
        }
    }, [dispatch, open, ideaId]);

    const handleClose = () => {
        dispatch(initializeDetail());
        onClose?.();
    };

    const handleReload = () => {
        if (ideaId != null) dispatch(readIdeaDetail({ ideaId }));
    };

    const isNotFound = detailErrorResponse?.status === 404;
    const hasError = !!detailErrorResponse && !isNotFound;
    const showContent = !loading && !detailErrorResponse && detailResponse;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth='md'
            scroll='paper'
            PaperProps={{ sx: { borderRadius: '12px' } }}
        >
            <DialogTitle sx={{ pr: 6, fontWeight: 700, color: '#1F1B3A' }}>
                {showContent ? (detailResponse.title || '제목 없음') : '아이디어 상세'}
                <IconButton
                    aria-label='close'
                    onClick={handleClose}
                    sx={{ position: 'absolute', right: 12, top: 12, color: 'text.secondary' }}
                >
                    <CloseRoundedIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ minHeight: 240 }}>
                {loading &&
                    <Box sx={{ py: 6 }}>
                        <MessageBox type='LOADING' loadingSize={32} />
                    </Box>
                }
                {isNotFound &&
                    <Box sx={{ py: 6 }}>
                        <MessageBox
                            Icon={WarningRoundedIcon}
                            message='아이디어를 찾을 수 없습니다.'
                        />
                    </Box>
                }
                {hasError &&
                    <Box sx={{ py: 6 }}>
                        <MessageBox
                            type='ERROR'
                            Icon={WarningRoundedIcon}
                            message='상세 정보를 불러오는 중 오류 발생'
                            handleReload={handleReload}
                        />
                    </Box>
                }
                {showContent &&
                    <Stack spacing={2.5}>
                        {detailResponse.summary &&
                            <Box>
                                <SectionLabel>제안 요지</SectionLabel>
                                <Typography
                                    variant='body2'
                                    sx={{
                                        color: 'text.primary',
                                        lineHeight: 1.7,
                                        whiteSpace: 'pre-wrap'
                                    }}
                                >
                                    {detailResponse.summary}
                                </Typography>
                            </Box>
                        }

                        <Divider />

                        <Box>
                            <SectionLabel>본문</SectionLabel>
                            {detailResponse.content
                                ? <Typography
                                    variant='body2'
                                    sx={{
                                        color: 'text.primary',
                                        lineHeight: 1.7,
                                        whiteSpace: 'pre-wrap'
                                    }}
                                >
                                    {detailResponse.content}
                                </Typography>
                                : <Typography variant='body2' sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                    본문이 등록되지 않았습니다.
                                </Typography>
                            }
                        </Box>

                    </Stack>
                }
            </DialogContent>
        </Dialog>
    );
}

function SectionLabel({ children }) {
    return (
        <Typography
            variant='caption'
            sx={{
                display: 'block',
                mb: 0.75,
                fontWeight: 700,
                color: 'text.secondary',
                letterSpacing: '0.04em'
            }}
        >
            {children}
        </Typography>
    );
}
